import type { Address } from '../types.js';

/**
 * A contact as Apple Pay hands it to `onpaymentauthorized`
 * (`event.payment.billingContact` / `event.payment.shippingContact`).
 *
 * Apple only fills the fields the payment request asked for, so an integration
 * that omits `requiredBillingContactFields` / `requiredShippingContactFields`
 * receives nothing here — see `applePayContactToAddress`.
 */
export interface ApplePayContact {
  givenName?: string;
  familyName?: string;
  addressLines?: string[];
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
  emailAddress?: string;
  phoneNumber?: string;
}

/** A Google Pay address (`paymentData.shippingAddress`, or `billingAddress`). */
export interface GooglePayContact {
  name?: string;
  address1?: string;
  address2?: string;
  address3?: string;
  locality?: string;
  administrativeArea?: string;
  postalCode?: string;
  countryCode?: string;
  phoneNumber?: string;
  emailAddress?: string;
}

/**
 * A contact as Stripe's Express Checkout Element reports it on `confirm`
 * (`event.billingDetails`, `event.shippingAddress`).
 */
export interface StripeContact {
  name?: string;
  email?: string;
  phone?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postal_code?: string;
    country?: string;
  };
}

/**
 * Map an Apple Pay contact onto an SDK `Address`.
 *
 * Returns `undefined` when the contact has no usable postal address, so a
 * caller can never overwrite a real address with an empty shell — Apple sends
 * a name-and-email-only contact whenever the payment request did not ask for
 * `postalAddress` (#TAG-1989).
 */
export function applePayContactToAddress(contact?: ApplePayContact | null): Address | undefined {
  if (!contact) return undefined;

  const address: Address = {
    firstName: contact.givenName,
    lastName: contact.familyName,
    line1: contact.addressLines?.[0],
    line2: contact.addressLines?.[1],
    city: contact.locality,
    state: contact.administrativeArea,
    postalCode: contact.postalCode,
    country: contact.countryCode,
    phone: contact.phoneNumber,
  };

  return isUsable(address) ? address : undefined;
}

/** Map a Google Pay address onto an SDK `Address`. Same contract as above. */
export function googlePayContactToAddress(contact?: GooglePayContact | null): Address | undefined {
  if (!contact) return undefined;

  const [firstName, ...rest] = (contact.name ?? '').trim().split(/\s+/);

  const address: Address = {
    firstName: firstName || undefined,
    lastName: rest.length ? rest.join(' ') : undefined,
    line1: contact.address1,
    line2: [contact.address2, contact.address3].filter(Boolean).join(' ') || undefined,
    city: contact.locality,
    state: contact.administrativeArea,
    postalCode: contact.postalCode,
    country: contact.countryCode,
    phone: contact.phoneNumber,
  };

  return isUsable(address) ? address : undefined;
}

/**
 * Map a Stripe Express Checkout contact onto an SDK `Address`. Same contract as
 * the wallet mappers above: no street line or no country means `undefined`.
 */
export function stripeContactToAddress(contact?: StripeContact | null): Address | undefined {
  if (!contact?.address) return undefined;

  const [firstName, ...rest] = (contact.name ?? '').trim().split(/\s+/);

  const address: Address = {
    firstName: firstName || undefined,
    lastName: rest.length ? rest.join(' ') : undefined,
    line1: contact.address.line1,
    line2: contact.address.line2,
    city: contact.address.city,
    state: contact.address.state,
    postalCode: contact.address.postal_code,
    country: contact.address.country,
    phone: contact.phone,
  };

  return isUsable(address) ? address : undefined;
}

/**
 * A street line and a country are the minimum a parcel — and every carrier
 * integration — needs. Anything less is a contact, not an address.
 */
function isUsable(address: Address): boolean {
  return Boolean(address.line1?.trim() && address.country?.trim());
}
